import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin } from "lucide-react";
import logo from "@/assets/logo.webp";

const socials = [
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=100064201080843", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/dhivisha__architects", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@dhivishaarchitects9782", label: "YouTube" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/dhivishaarchitects", label: "LinkedIn" },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const Footer = () => {
  return (
    <footer className="bg-secondary text-secondary-foreground">
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <motion.div variants={itemVariants}>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={logo} alt="Dhivisha Architects" className="h-12 w-auto drop-shadow-[0_2px_8px_hsl(40,50%,48%,0.3)]" />
              <div>
                <span className="text-xl font-bold text-primary block uppercase tracking-[0.15em] body-sans">DHIVISHA</span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-secondary-foreground/60 font-medium">ARCHITECTS</span>
              </div>
            </Link>
            <p className="text-secondary-foreground/70 text-sm leading-relaxed">
              Crafting spaces that inspire. Our perspective on architecture — what we strive to create, and how our services contribute to society.
            </p>
            <div className="flex gap-3 mt-5">
              {socials.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-9 h-9 rounded bg-secondary-foreground/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground active:scale-95 transition-all">
                  <s.icon size={16} />
                </a>
              ))}
              <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" aria-label="Pinterest" className="w-9 h-9 rounded bg-secondary-foreground/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground active:scale-95 transition-all">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 12a4 4 0 1 1 8 0c0 4-2 8-4 8"/><path d="M9.5 15.5 8 22"/></svg>
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-primary mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { label: "Home", path: "/" },
                { label: "About Us", path: "/about" },
                { label: "Our Projects", path: "/projects" },
                { label: "Services", path: "/services" },
                { label: "Contact Us", path: "/contact" },
                { label: "FAQ", path: "/faq" },
              ].map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-sm text-secondary-foreground/70 hover:text-primary transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Project Categories */}
          <motion.div variants={itemVariants}>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-primary mb-4">Project Types</h4>
            <ul className="space-y-2">
              {["Interior", "Residential", "Commercial", "Institutional", "Industrial", "Cultural", "Renovation", "Unique"].map((cat) => (
                <li key={cat}>
                  <Link to="/projects" className="text-sm text-secondary-foreground/70 hover:text-primary transition-colors">
                    {cat} Projects
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div variants={itemVariants}>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-primary mb-4">Get in Touch</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-secondary-foreground/70">
                <Phone size={16} className="text-primary shrink-0" />
                <div>
                  <span>+91 99948 49332</span><br />
                  <span>+91 84280 77535</span>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm text-secondary-foreground/70">
                <Mail size={16} className="mt-0.5 text-primary shrink-0" />
                <div>
                  <span>mail@dhivishaarchitects.com</span>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm text-secondary-foreground/70">
                <MapPin size={16} className="mt-0.5 text-primary shrink-0" />
                <span>No:9, Needamangalam Rd, Elanga Nagar, Srinivas Nagar, Sakkottai, Kumbakonam, Tamil Nadu 612001</span>
              </li>
            </ul>
          </motion.div>
        </div>

        <div className="mt-12 pt-8 border-t border-secondary-foreground/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 items-center">
            <p className="text-xs text-secondary-foreground/50">
              © {new Date().getFullYear()} Dhivisha Architects. All rights reserved.
            </p>
            <span className="hidden sm:inline text-xs text-secondary-foreground/30">|</span>
            <p className="text-xs text-secondary-foreground/50">
              Created by{" "}
              <a
                href="https://www.smsin360.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Smart Marketing System
              </a>
            </p>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-xs text-secondary-foreground/50 hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-xs text-secondary-foreground/50 hover:text-primary transition-colors">Terms of Service</Link>
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
